/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { useState, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { User } from '@supabase/supabase-js' 
import { useTranslations, useLocale } from 'next-intl'
import Image from 'next/image'
import { v4 as uuidv4 } from 'uuid'
import dynamic from 'next/dynamic'

// dynamic import for GooglePlacesAutocomplete
const GooglePlacesAutocomplete = dynamic(() => import('@/components/GooglePlacesAutocomplete'), { ssr: false })
const DynamicPrice = dynamic(() => import('@/components/DynamicPrice'), { ssr: false })
const CryptoMethod = dynamic(() => import('./payment-methods/crypto'), { ssr: false })
const WireMethod = dynamic(() => import('./payment-methods/wire'), { ssr: false })

import { Link } from '@/i18n/routing'
import { createClient } from '@/app/[locale]/utils/supabase/client' // Updated import
import { Product } from '@/types/database' 
import { useCartStore, useCheckoutStore, ShippingAddress } from '@/stores/cartStore'
import { getCurrencyFromLocale, convertPriceWithRate, formatCurrency } from '@/i18n/utils'
import { SiteSettings } from '@/types/database'
import { updateI18nConfig } from '@/i18n/site-settings-integration'
import { updateProfileVatNumber, validateVatNumber, applyCoupon } from './checkoutActions' // Import server actions and coupon action
import { checkAndFilterRestrictedProducts, CartItemForRestrictionCheck } from './checkoutActions'; // Import the new server action and type
import type { TruevoResponse } from './truevoCheckout'
import TruevoMethod from './payment-methods/truevo'
import { CouponInput } from './CouponInput' // Import the new component
import { ErrorBoundary } from 'react-error-boundary'

// Add a ClientOnly wrapper component to handle hydration issues
function ClientOnly({ 
  children, 
  ...delegated 
}: { 
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  const [hasMounted, setHasMounted] = useState(false);
  
  useEffect(() => {
    setHasMounted(true);
  }, []);
  
  if (!hasMounted) {
    return null;
  }
  
  return <div {...delegated}>{children}</div>;
}

interface ExtendedProduct extends Product {
  options?: string | ProductOption[];
}

interface CartItem {
  id?: string;
  product: ExtendedProduct;
  quantity: number;
  selectedColor?: string;
}

interface CheckoutClientProps {
  initialCartItems: CartItem[];
  user: User | null;
  initialProfile: {
    company_name: string;
    address_line1?: string;
    address_line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    vat_number?: string;
  };
  initialTranslatedNames: Record<string, string>;
  initialTranslatedSlugs: Record<string, string>;
  isB2B?: boolean; // B2B mode flag
  siteSettings: SiteSettings;
  vatNumber: string;
}

// Define interfaces for product options and checkout cart item
interface ProductOption {
  name: string;
  extraPrice: number;
}

// New interface
interface CheckoutCartItem {
  id?: string;
  quantity: number;
  selectedColor?: string;
  product: ExtendedProduct;
}

// Inserted helper functions for price calculation in checkout
function calculatePricePerKg(quantityGrams: number, priceChart: Record<string, number> | undefined, basePrice: number): number {
  try {
    // Validate inputs
    if (typeof basePrice !== 'number' || isNaN(basePrice)) {
      return 0;
    }
    
    // If no price chart, return base price
    if (!priceChart) return basePrice;
    
    // Ensure quantityGrams is a valid number
    if (typeof quantityGrams !== 'number' || isNaN(quantityGrams) || quantityGrams <= 0) {
      return basePrice;
    }
    
    const quantityKg = quantityGrams / 1000;
    
    // Validate price chart
    if (typeof priceChart !== 'object' || priceChart === null || Object.keys(priceChart).length === 0) {
      return basePrice;
    }
    
    // Sort price ranges
    const ranges = Object.entries(priceChart)
      .filter(([range]) => typeof range === 'string' && range.trim().length > 0) // Filter out invalid ranges
      .sort((a, b) => {
        try {
          const aLower = parseFloat(a[0].split('-')[0]);
          const bLower = parseFloat(b[0].split('-')[0]);
          return isNaN(aLower) || isNaN(bLower) ? 0 : aLower - bLower;
        } catch {
          return 0; // Handle parsing errors
        }
      });
    
    if (ranges.length === 0) return basePrice;
    
    // Find applicable price tier - Fix for small quantities (100g)
    for (const [range, price] of ranges) {
      try {
        if (!range) continue;
        
        const bounds = range.replace('kg', '').split('-').map(n => parseFloat(n.trim()));
        
        if (!bounds || bounds.length === 0) continue;
        
        const lower = bounds[0];
        const upper = bounds.length > 1 ? bounds[1] : null;
        
        if (isNaN(lower)) continue; // Skip invalid ranges
        
        // Special handling for samples (100g or less)
        if (quantityGrams <= 100 && lower === 0) {
          return typeof price === 'number' ? price : basePrice;
        }
        
        // Normal tier check
        if (quantityKg >= lower && (!upper || isNaN(upper) || quantityKg <= upper)) {
          return typeof price === 'number' ? price : basePrice;
        }
      } catch (error) {
        console.error('Error parsing price range:', error, range);
        continue;
      }
    }
    
    return basePrice;
  } catch (error) {
    console.error('Error calculating price per kg:', error);
    return basePrice; // Return basePrice as fallback
  }
}

// Add a new function to calculate the price for API cart items
function calculateApiItemPrice(item: any): number {
  try {
    // Check if item and product exist and have required properties
    if (!item || !item.product || typeof item.product.price_per_kg !== 'number') {
      return 0;
    }

    // Ensure quantity is a valid number
    const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
    if (quantity <= 0) {
      return 0;
    }

    // Check if product is priced per kg or per unit
    const isKgPrice = item.product.kg_price !== false;
    
    // Convert grams to kg only for kg-priced products
    const quantityKg = isKgPrice ? quantity / 1000 : quantity;
    
    const basePricePerKg = calculatePricePerKg(
      quantity, 
      item.product?.price_chart, 
      item.product.price_per_kg
    );
    
    let extraPrice = 0;
    
    // Check for selected color and options
    if (item.selectedColor && item.product.options) {
      let options = item.product.options;
      
      // Handle string options by parsing them
      if (typeof options === 'string') {
        try {
          options = JSON.parse(options);
        } catch (error) {
          console.error('Error parsing product options:', error);
          options = [];
        }
      }
      
      // Find the selected option and add its extra price
      if (Array.isArray(options)) {
        const selectedOption = options.find((opt: any) => opt && opt.name === item.selectedColor);
        if (selectedOption && typeof selectedOption.extraPrice === 'number') {
          extraPrice = selectedOption.extraPrice;
        }
      }
    }
    
    const finalPricePerKg = basePricePerKg + extraPrice;
    
    // For kg-priced products, multiply by kg quantity
    // For unit-based products, multiply by unit quantity
    return isKgPrice ? quantityKg * finalPricePerKg : finalPricePerKg * quantity;
  } catch (error) {
    console.error('Error calculating item price:', error);
    return 0; // Return 0 instead of throwing an error
  }
}

// Define a type for the intermediate result before filtering
type CartItemOrNull = { 
  id?: string; 
  quantity: number; 
  selectedColor?: string; 
  product: ExtendedProduct; 
} | null;

// Add this helper function to work with Google Places Autocomplete
const updateAddressFromPlacesApi = (address: any, setAddress: React.Dispatch<React.SetStateAction<ShippingAddress>>) => {
  setAddress(prev => ({
    ...prev,
    address_line1: address.address_line1 || prev.address_line1,
    address_line2: address.address_line2 || prev.address_line2,
    city: address.city || prev.city,
    state: address.state || prev.state,
    postal_code: address.postal_code || prev.postal_code,
    country: address.country || prev.country
  }));
};

function ErrorFallback({ error, resetErrorBoundary }: { error: Error; resetErrorBoundary: () => void }) {
  return (
    <div role="alert" className="p-4 border border-red-500 rounded-md">
      <p className="text-red-500 font-medium">Something went wrong with the address autocomplete:</p>
      <pre className="mt-2 text-sm text-red-400">{error.message}</pre>
      <button
        onClick={resetErrorBoundary}
        className="mt-2 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
      >
        Try again
      </button>
    </div>
  );
}

export default function CheckoutClient({
  initialCartItems,
  user,
  initialProfile,
  initialTranslatedNames,
  initialTranslatedSlugs,
  isB2B,
  siteSettings,
  vatNumber: initialVatNumber
}: CheckoutClientProps) {
  const t = useTranslations('checkout')
  const locale = useLocale()
  const userCurrency = getCurrencyFromLocale(locale)
  const router = useRouter()
  const [cartItems, setCartItems] = useState<CartItem[]>(initialCartItems)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const supabase = createClient() // Client-side Supabase client
  // Get country translations
  const countryT = useTranslations('countries')
  
  // Get checkout store values and actions
  const checkoutState = useCheckoutStore();
  
  // Add hydration status tracking
  const [isHydrated, setIsHydrated] = useState(false);
  const hasPreviouslyHydrated = useRef(false);
  
  // Add refs to track previous values
  const prevEmail = useRef('');
  const prevPhone = useRef('');
  const prevCompanyName = useRef('');
  const prevVatNumber = useRef('');
  const prevVatValid = useRef(false);
  const prevPaymentMethod = useRef<'wire' | 'crypto' | 'truevo'>('truevo');
  const prevShippingAddress = useRef<ShippingAddress>({
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    notes: ''
  });

  // Initialize email state from checkoutStore or user email - moved below hydration check
  const [email, setEmail] = useState<string>('');
  
  // Add state for form validation
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [formTouched, setFormTouched] = useState<Record<string, boolean>>({})
  const [formValid, setFormValid] = useState(false)
  
  // Get countries list from translations
  const countries = [
    "Canada",
    "Czechia",
    "Denmark",
    "Finland",
    "France",
    "Germany",
    "Ireland",
    "Italy",
    "Japan",
    "Netherlands",
    "Poland",
    "Spain",
    "Sweden",
    "United Kingdom",
    "United States"
  ]
  
  // Replace the existing useEffect for translations with initialization from props
  const translatedNames = useMemo(() => initialTranslatedNames || {}, [initialTranslatedNames]);
  const translatedSlugs = useMemo(() => initialTranslatedSlugs || {}, [initialTranslatedSlugs]);

  const { items, clearCart } = useCartStore() // Add clearCart function

  // Add notification state for restricted products
  const [restrictedProductsNotification, setRestrictedProductsNotification] = useState<string | null>(null)

  // Initialize shipping address with empty values first
  const [shippingAddress, setShippingAddress] = useState<ShippingAddress>({
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    notes: ''
  })
  
  // Initialize with empty values
  const [vatNumber, setVatNumber] = useState<string>(initialVatNumber || '');
  const [vatValid, setVatValid] = useState<boolean>(false);
  const [vatError, setVatError] = useState('');
  const [companyName, setCompanyName] = useState('');
  // Store the original VAT-validated company name separately
  const [vatValidatedCompanyName, setVatValidatedCompanyName] = useState('');
  
  // Initialize with default payment method
  const [paymentMethod, setPaymentMethod] = useState<'wire' | 'crypto' | 'truevo'>('truevo')
  // Track the truevoCheckoutKey for forcing remounts
  const [truevoCheckoutKey, setTruevoCheckoutKey] = useState<number>(Date.now());
  
  // Initialize phone state from store
  const [phone, setPhone] = useState('');

  // Coupon application state
  const [couponCode, setCouponCode] = useState<string>('');
  const [isApplyingCoupon, setIsApplyingCoupon] = useState<boolean>(false);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponId, setCouponId] = useState<string | null>(null);
  const [discountAmount, setDiscountAmount] = useState<number>(0);
  // Track if coupon has hit usage limit to disable further attempts
  const [couponUsed, setCouponUsed] = useState<boolean>(false);

  // Add billing address state
  const [billingIsSameAsShipping, setBillingIsSameAsShipping] = useState<boolean>(true);
  const [billingAddress, setBillingAddress] = useState<ShippingAddress>({
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    notes: ''
  });

  // Add a ref to track previous value of billingIsSameAsShipping
  const prevBillingIsSameAsShipping = useRef<boolean>(true);
  // Add a ref to track previous billing address values
  const prevBillingAddress = useRef<ShippingAddress>({
    company_name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    notes: ''
  });

  // Add a new state variable to track order placement
  const [orderJustPlaced, setOrderJustPlaced] = useState(false);

  // Track the last validated VAT number
  const [lastValidatedVat, setLastValidatedVat] = useState<string>('');

  // Show verified tick if:
  // - VAT number matches profile VAT (from initialProfile)
  // - OR VAT number matches last validated value
  const showVatVerifiedTick = (
    vatNumber.trim() !== '' && (
      vatNumber === (initialProfile?.vat_number || '') ||
      vatNumber === lastValidatedVat
    )
  );

  // When VAT is validated via API, update lastValidatedVat
  const handleVatBlur = async () => {
    const trimmed = vatNumber.trim();

    // Early exit – empty field
    if (!trimmed) {
      setVatError(t('shipping.vatStatus.empty'));
      setVatValid(false);
      return;
    }

    // Skip validation if the value matches the one already stored on the profile
    if (trimmed === (initialProfile?.vat_number || '')) {
      setVatValid(true);
      setVatError('');
      return;
    }

    const result = await validateVatNumber(trimmed);

    if (result.isValid) {
      setVatValid(true);
      setVatError('');

      const validatedCompanyName = result.companyName || '';

      // Store for later reference
      setVatValidatedCompanyName(validatedCompanyName);

      // Update company names depending on billing configuration
      if (billingIsSameAsShipping) {
        setCompanyName(validatedCompanyName);
      } else {
        setBillingAddress(prev => ({
          ...prev,
          company_name: validatedCompanyName,
        }));
      }

      // Persist VAT number to profile if the user is logged in
      if (user?.id) {
        const updateRes = await updateProfileVatNumber(user.id, trimmed);
        if (updateRes.error) {
          console.error('Error updating VAT number in profile:', updateRes.error);
        }
      }

      // Finally, remember this VAT number as validated for UI purposes
      setLastValidatedVat(trimmed);
    } else {
      // Map error codes to translation keys
      const errorKey = result.error === 'invalid' ? 'invalid' : result.error === 'empty' ? 'empty' : 'error';
      setVatError(t(`shipping.vatStatus.${errorKey}`));
      setVatValid(false);
    }
  };

  // Mark component as hydrated after first render
  useEffect(() => {
    setIsHydrated(true);
  }, []);
  
  // Set initial values from checkout store after hydration
  useEffect(() => {
    if (!isHydrated || hasPreviouslyHydrated.current) return;
    
    // Set flag to ensure this only runs once after the first hydration
    hasPreviouslyHydrated.current = true;
  
    
    // Prioritize logged-in user's email over cached email
    if (user?.email) {
      setEmail(user.email);
    } else {
      setEmail(checkoutState.email || '');
    }
    
    setPhone(checkoutState.phone || '');
    
    const initialVat = checkoutState.vatNumber || (initialProfile.vat_number || '');
    const plainVat = initialVat.startsWith('valid:') ? initialVat.substring(6) : initialVat;
    const isVatValid = checkoutState.vatValid || initialVat.startsWith('valid:');
    setVatNumber(plainVat);
    setVatValid(isVatValid);
    
    // Set the validated company name if it exists in the store
    const validatedCompany = checkoutState.vatValidatedCompanyName || 
      (isVatValid && checkoutState.companyName ? checkoutState.companyName : '');
    
    if (validatedCompany) {
      setVatValidatedCompanyName(validatedCompany);
    }
    
    // Set company name prioritizing validated company name for consistency
    setCompanyName(validatedCompany || checkoutState.companyName || initialProfile.company_name || '');
    
    setPaymentMethod(checkoutState.preferredPaymentMethod || 'truevo');
    
    setShippingAddress({
      company_name: checkoutState.shippingAddress.company_name || initialProfile.company_name || '',
      address_line1: checkoutState.shippingAddress.address_line1 || initialProfile.address_line1 || '',
      address_line2: checkoutState.shippingAddress.address_line2 || initialProfile.address_line2 || '',
      city: checkoutState.shippingAddress.city || initialProfile.city || '',
      state: checkoutState.shippingAddress.state || initialProfile.state || '',
      postal_code: checkoutState.shippingAddress.postal_code || initialProfile.postal_code || '',
      country: checkoutState.shippingAddress.country || initialProfile.country || '',
      notes: checkoutState.shippingAddress.notes || ''
    });
    
    // Initialize billing address and same-as-shipping flag
    if (checkoutState.billingIsSameAsShipping !== undefined) {
      setBillingIsSameAsShipping(checkoutState.billingIsSameAsShipping);
    }
    
    // Only set billing address if it exists in the store and has some data
    const hasBillingAddress = checkoutState.billingAddress && (
      checkoutState.billingAddress.company_name || 
      checkoutState.billingAddress.address_line1 || 
      checkoutState.billingAddress.city
    );
    
    if (hasBillingAddress) {
      setBillingAddress({
        company_name: checkoutState.billingAddress.company_name || '',
        address_line1: checkoutState.billingAddress.address_line1 || '',
        address_line2: checkoutState.billingAddress.address_line2 || '',
        city: checkoutState.billingAddress.city || '',
        state: checkoutState.billingAddress.state || '',
        postal_code: checkoutState.billingAddress.postal_code || '',
        country: checkoutState.billingAddress.country || '',
        notes: checkoutState.billingAddress.notes || ''
      });
    } else if (isVatValid && validatedCompany) {
      // If we have a valid VAT with company name but no billing address, set the company name
      setBillingAddress(prev => ({
        ...prev,
        company_name: validatedCompany
      }));
    }    
  }, [isHydrated,
    user,
    initialProfile,
    checkoutState.email,
    checkoutState.phone,
    checkoutState.vatNumber,
    checkoutState.vatValid,
    checkoutState.vatValidatedCompanyName,
    checkoutState.companyName,
    checkoutState.preferredPaymentMethod,
    checkoutState.shippingAddress,
    checkoutState.billingIsSameAsShipping,
    checkoutState.billingAddress]); // Keep dependencies but use ref to prevent re-execution

  const cartItemsFromStore = useCartStore((state) => state.items);
  const storeInitialProducts = useCartStore((state) => state.initialProducts);
  const initialProducts = useMemo(() => storeInitialProducts || [], [storeInitialProducts]);
  const enrichedCartItems: CheckoutCartItem[] = cartItemsFromStore.map((item: any): CartItemOrNull => {
    try {
      const product = initialProducts.find((p: any) => p.id === item.productId) as ExtendedProduct;
      if (!product) {
        return null;
      }
      
      return { 
        id: item.id || uuidv4(), // Use existing ID or generate a new one
        quantity: item.quantity,
        selectedColor: item.selectedColor,
        product 
      };
    } catch {
      return null;
    }
  }).filter((item): item is CheckoutCartItem => item !== null);

  // Add a loading state
  const [isLoading, setIsLoading] = useState(true);

  // Update the useEffect for cart initialization - this is critical for persistence
  useEffect(() => {
    // Only initialize on the client side after hydration
    if (!isHydrated) return;

    const initializeCartData = async () => {
      setIsLoading(true);
      
      try {
        
        // Always initialize the cart store to ensure persistence
        await useCartStore.getState().initializeCart(user?.id);
        
        // After initialization, check again
        const updatedStoreItems = useCartStore.getState().items;
        
        // If we're logged in and have server-provided initialCartItems, use those
        if (user && initialCartItems && initialCartItems.length > 0) {
          const itemsWithIds = initialCartItems.map((item: any) => ({
            ...item,
            id: item.id || uuidv4()
          }));
          setCartItems(itemsWithIds);
        } 
        // For guest users or when no initialCartItems, prioritize the Zustand store
        else if (updatedStoreItems && updatedStoreItems.length > 0) {
          // The cart is already loaded in the store, so we're good
        } else {
          // No items in store or from server
        }
      } catch (error) {
        console.error('[CheckoutClient] Error initializing cart:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeCartData();
  }, [isHydrated, user, initialCartItems]); // Remove checkoutState.email and checkoutState.shippingAddress from dependencies

  const previousShippingCountryRef = useRef<string | undefined>(undefined);
  const lastProcessedCartRef = useRef<string | undefined>(undefined);

  // Effect to update cart when shipping country changes or cart changes
  useEffect(() => {
    if (!isHydrated || !shippingAddress.country) {
      if (!shippingAddress.country) previousShippingCountryRef.current = undefined;
      return;
    }

    const currentCountry = shippingAddress.country;
    const previousCountry = previousShippingCountryRef.current;
    const currentCartState = useCartStore.getState().items;
    const cartHash = JSON.stringify(currentCartState.map(item => ({
      productId: item.productId,
      quantity: item.quantity,
      selectedColor: item.selectedColor
    })));

    // Only process if the country has changed, or the cart has changed since last processing for this country
    if (
      currentCountry !== previousCountry ||
      lastProcessedCartRef.current !== cartHash
    ) {
      previousShippingCountryRef.current = currentCountry;
      lastProcessedCartRef.current = cartHash;
      // Country changed from previousCountry to currentCountry or cart changed. Processing.
      const processRestrictedProducts = async () => {
        const { removeItem } = useCartStore.getState();
        // Use the latest items from store
        const itemsToCheck: CartItemForRestrictionCheck[] = currentCartState.map((cartStoreItem: any) => {
          const productDetail = initialProducts.find((p: any) => p.id === cartStoreItem.productId);
          return {
            id: cartStoreItem.id || uuidv4(),
            productId: cartStoreItem.productId,
            productName: productDetail ? (translatedNames[productDetail.id] || productDetail.name || `Product #${productDetail.id.substring(0, 8)}`) : 'Unknown Product',
            quantity: cartStoreItem.quantity,
            selectedColor: cartStoreItem.selectedColor,
          };
        }).filter(item => item.productId);

        if (itemsToCheck.length === 0) {
          setRestrictedProductsNotification(null);
          return;
        }

        try {
          const { filteredItems, removedProductNames } = await checkAndFilterRestrictedProducts(
            itemsToCheck,
            currentCountry // Use currentCountry explicitly
          );

          const removedIds = itemsToCheck
            .filter(originalItem => !filteredItems.some(filteredApiItem => filteredApiItem.id === originalItem.id))
            .map(item => item.productId);

          removedIds.forEach(productId => removeItem(productId));

          if (removedProductNames.length > 0) {
            const message = t('restricted.productsRemoved', {
              country: countryT(currentCountry), // Use currentCountry
              products: removedProductNames.join(', ')
            });
            setRestrictedProductsNotification(message);
            setTimeout(() => setRestrictedProductsNotification(null), 10000);
          } else {
            setRestrictedProductsNotification(null);
          }
        } catch (error) {
          console.error("Error checking/filtering restricted products:", error);
          setRestrictedProductsNotification(t('errors.restrictionCheckFailed'));
          setTimeout(() => setRestrictedProductsNotification(null), 10000);
        }
      };
      processRestrictedProducts();
    }
    // No else needed
  }, [shippingAddress.country, isHydrated, initialProducts, t, countryT, translatedNames]);

  // Update the displayItems logic to reflect changes from server action
  const displayItems = useMemo(() => {
    // displayItems should primarily reflect the state of cartItems, 
    // which is updated by the useEffect hook above after calling checkAndFilterRestrictedProducts.
    // The enrichedCartItems from the store will also be updated by the removeItem calls.
    
    // If enrichedCartItems (from Zustand store) is populated, prefer it.
    if (enrichedCartItems && enrichedCartItems.length > 0) {
        return enrichedCartItems.filter(item => item && item.product); // Ensure product exists
    }
    // Otherwise, use the local cartItems state (which should be in sync)
    if (cartItems && cartItems.length > 0) {
      return cartItems
        .filter(item => item && item.product) 
        .map(item => ({
          id: item.id || uuidv4(), 
          quantity: item.quantity,
          selectedColor: item.selectedColor,
          product: item.product as ExtendedProduct
        }));
    }
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrichedCartItems, cartItems, shippingAddress.country]); // shippingAddress.country might be redundant if cartItems is always up-to-date

  function getExtraPrice(item: CheckoutCartItem): number {
    try {
      if (!item || !item.product) {
        return 0;
      }
      
      let extraPrice = 0;
      const options = (item.product as ExtendedProduct).options;
      
      if (!item.selectedColor || !options) {
        return 0;
      }
      
      let parsedOptions = options;
      if (typeof options === 'string') {
        try {
          parsedOptions = JSON.parse(options);
        } catch {
          return 0;
        }
      }
      
      if (Array.isArray(parsedOptions)) {
        const selectedOption = parsedOptions.find((opt: any) => opt.name === item.selectedColor);
        if (selectedOption && typeof selectedOption.extraPrice === 'number' && !isNaN(selectedOption.extraPrice)) {
          extraPrice = selectedOption.extraPrice;
        }
      }
      
      return extraPrice;
    } catch {
      return 0;
    }
  }
  useEffect(() => {
    if (!user && items.length > 0) {
      const fetchProducts = async () => {
        const productIds = items.map(item => item.productId)
        if (productIds.length === 0) return
        
        const { data: products } = await supabase
          .from('products')
          .select('*')
          .in('id', productIds)
        
        if (products) {
          const cartItemsWithProducts = items.map(item => {
            const product = products.find(p => p.id === item.productId)
            return {
              quantity: item.quantity,
              product: product as ExtendedProduct
            }
          }).filter(item => item.product)
          
          setCartItems(cartItemsWithProducts)
        }
      }
      
      fetchProducts()
    }
  }, [user, items, supabase])


  useEffect(() => {
    if (initialCartItems.length > 0) {
      const itemsWithIds = initialCartItems.map((item: any) => ({
        ...item,
        id: item.id || uuidv4()
      }));
      setCartItems(itemsWithIds);
    }
  }, [initialCartItems]);

  const availablePaymentMethods = [
    { id: 'truevo', name: t('payment.methods.card'), icon: '💳' },
    { id: 'wire', name: t('payment.methods.wire'), icon: '🏦' },
    { id: 'crypto', name: t('payment.methods.crypto'), icon: '⛓️' }
  ];
  
  const countryCodeMap: Record<string, string> = {
    'United Kingdom': 'GB',
    'United States': 'US',
    'Germany': 'DE',
    'France': 'FR',
    'Italy': 'IT',
    'Spain': 'ES',
    'Netherlands': 'NL',
    'Belgium': 'BE',
    'Ireland': 'IE',
    'Finland': 'FI',
    'Sweden': 'SE',
    'Denmark': 'DK',
    'Poland': 'PL',
    'Czechia': 'CZ',
    'Canada': 'CA',
    'Japan': 'JP'
  };
  
  // Helper to get net total (ex-VAT) for fee calculation
  function getNetTotal({ baseTotal, isB2B, isVatExempt, isVatExemptB2B }: { baseTotal: number; isB2B: boolean; isVatExempt: boolean; isVatExemptB2B: boolean }): number {
    // B2B: prices are net, so baseTotal is already net
    // B2C: if VAT valid (exempt), net = gross / 1.25
    if (isB2B && isVatExemptB2B && !isCryptoPayment) return baseTotal;
    return isVatExempt ? baseTotal / 1.25 : baseTotal;
  }
  
  // Add computed totals before the return statement
  const baseTotal = useMemo(() => {
    return displayItems.filter(item => item && item.product)
      .reduce((sum, item) => sum + calculateApiItemPrice(item), 0);
  }, [displayItems]);
  
  // Apply coupon discount to subtotal (subtract net discount on gross for B2C)
  const discountedBaseTotal = Math.max(
    baseTotal - (isB2B ? discountAmount : discountAmount * 1.25),
    0
  );

  // --- VAT Exemption Logic ---
  const isCryptoPayment = paymentMethod === 'crypto';
  // Only consider VAT valid when explicitly validated via API
  const effectiveVatNumber = vatNumber.trim();
  const isVatValidForDisplay = vatValid && effectiveVatNumber !== '';
  // For B2C, VAT is exempt if not B2B and VAT is valid for display
  const isVatExemptB2C = !isB2B && isVatValidForDisplay && !isCryptoPayment;
  const isVatExemptB2B = isB2B && isVatValidForDisplay && !isCryptoPayment;

  // Compute net subtotal before discount (for display)
  const netSubtotalBeforeDiscount = useMemo(() => {
    if (isB2B) return baseTotal;
    return baseTotal / 1.25;
  }, [baseTotal, isB2B]);

  // Use isVatExemptB2C for netTotal calculation
  const netTotal = useMemo(() => {
    return getNetTotal({ 
      baseTotal: discountedBaseTotal, 
      isB2B: !!isB2B, 
      isVatExempt: isVatExemptB2C,
      isVatExemptB2B: isVatExemptB2B || false
    })
  }, [discountedBaseTotal, isB2B, isVatExemptB2C, isVatExemptB2B]);
  const feeAmount = useMemo(() => netTotal * (paymentMethod === 'truevo' ? 0.05 : isCryptoPayment ? 0.10 : 0), [netTotal, paymentMethod, isCryptoPayment]);
  // VAT amount: zero if exempt for either B2C or B2B, otherwise 25% for B2B, 20% for B2C
  const vatAmount = (isVatExemptB2C || isVatExemptB2B)
    ? 0
    : isB2B
      ? discountedBaseTotal * 0.25
      : discountedBaseTotal * 0.2;
  const finalTotal = netTotal + feeAmount + vatAmount;

  const handleCheckout = async () => {
    // Check if cart is empty first
    if (!displayItems || displayItems.length === 0) {
      setError(t('errors.emptyCart'));
      router.push(`/${locale}/products`);
      return;
    }
    
    // Check form validity first
    if (!formValid) {
      setError(t('errors.missingFields'));
      // Mark all fields as touched to show errors
      const allTouched: Record<string, boolean> = {};
      Object.keys(formErrors).forEach(field => {
        allTouched[field] = true;
      });
      setFormTouched(allTouched);
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      const currentUser = user ? { id: user.id } : null;
      const apiCartItems = displayItems
        .filter(item => item && item.product) // Ensure item and product exist
        .map(item => ({
          product: item.product,
          quantity: item.quantity,
          selectedColor: item.selectedColor
        }));
      
      // If after filtering there are no valid items, redirect to products
      if (apiCartItems.length === 0) {
        setError(t('errors.emptyCart'));
        setIsSubmitting(false);
        router.push(`/${locale}/products`);
        return;
      }
      
      const finalShippingAddress = {
        ...shippingAddress,
        company_name: companyName || shippingAddress.company_name
      };
      
      // Use billing address if it's not the same as shipping
      const finalBillingAddress = billingIsSameAsShipping ? finalShippingAddress : billingAddress;
      
      // Add VAT number to addresses if valid
      if (vatNumber.trim() && vatValid) {
        // Add vat_number to the addresses
        finalShippingAddress.vat_number = vatNumber.trim();
        if (!billingIsSameAsShipping && finalBillingAddress) {
          finalBillingAddress.vat_number = vatNumber.trim();
        }
      }
      
      // Get currency information based on the user's locale
      const userCurrency = getCurrencyFromLocale(locale);
      // Get the translated payment method name
      const paymentMethodTranslations = {
        'truevo': t('payment.methods.card'),
        'wire': t('payment.methods.wire'),
        'crypto': t('payment.methods.crypto')
      };
      const translatedPaymentMethod = paymentMethodTranslations[paymentMethod] || paymentMethod;
      // Use netTotal and feeAmount for VAT-exempt B2C
      const requestPayload = {
        user: currentUser,
        cartItems: apiCartItems,
        shippingAddress: finalShippingAddress,
        billingAddress: finalBillingAddress,
        paymentMethod: paymentMethod,
        paymentMethodDisplayName: translatedPaymentMethod,
        isGuestCheckout: !user,
        email,
        locale,
        vat_number: isVatValidForDisplay ? effectiveVatNumber : undefined,
        currency: userCurrency,
        totalAmount: finalTotal,
        // Include coupon code and discount amount
        coupon: couponId ? couponCode.trim() : null,
        coupon_discount: couponId ? (isB2B ? discountAmount : discountAmount * 1.25).toFixed(2) : null,
        payment_details: paymentMethod === 'crypto' ? cryptoDetails : 
                         paymentMethod === 'wire' ? wireDetails : undefined,
        // Flag to indicate if this order is VAT exempt (never for crypto payments)
        isVatExempt: isVatValidForDisplay && !isCryptoPayment
      };
      
      const apiResponse = await fetch('/api/createOrder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestPayload)
      });
      
      if (!apiResponse.ok) {
        const errorData = await apiResponse.json();
        throw new Error(errorData.error || 'Failed to create order');
      }
      
      const result = await apiResponse.json();
      
      // For crypto payments, redirect to the hosted Coinbase Commerce page if provided
      if (paymentMethod === 'crypto' && result.hostedUrl) {
        window.location.href = result.hostedUrl;
        return;
      }
      
      if (apiResponse.ok && result.success) {
        // Set the flag that an order was just placed before clearing the cart
        setOrderJustPlaced(true);
        
        // Clear the cart on successful order
        clearCart();
        
        // For guest users, include the orderToken in the URL
        if (!user && result.orderToken) {
          router.push(`/${locale}/orders/${result.orderId}?token=${encodeURIComponent(result.orderToken)}`);
        } else {
          // For logged in users, just use the order ID
          router.push(`/${locale}/orders/${result.orderId}`);
        }
      } else {
        // Check if this is a restricted products error
        if (result.restrictedProducts && Array.isArray(result.restrictedProducts)) {
          // Remove the restricted products from the cart
          const { removeItem } = useCartStore.getState();
          result.restrictedProducts.forEach((productId: string) => {
            removeItem(productId);
          });
          
          // Set a more specific error message
          setError(result.error || t('restricted.cannotShip', {
            country: shippingAddress.country
          }));
        } else {
          // Set the generic error message
          setError(result.error || t('errors.createOrderFailed', {}));
        }
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error('Checkout error:', err);
      setError(t('errors.generalError'));
      setIsSubmitting(false);
    }
  };

  // Using the existing prevPaymentMethod reference instead of creating a new one
  useEffect(() => {
    if (paymentMethod === 'truevo' && prevPaymentMethod.current !== 'truevo') {
      setTruevoCheckoutKey(Date.now());
    }
    prevPaymentMethod.current = paymentMethod;
  }, [paymentMethod]);

  // Add effect to validate form whenever relevant fields change
  useEffect(() => {
    const errors: Record<string, string> = {};
    
    // Validate email
    if (!email) {
      errors.email = t('errors.required');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('errors.invalidEmail');
    }
    
    // Validate shipping address fields
    if (!companyName) {
      errors.companyName = t('errors.required');
    }
    
    // Validate VAT number - only required in B2B mode and not for bank transfer
    if (isB2B && paymentMethod !== 'wire' && !vatNumber.trim()) {
      errors.vatNumber = t('errors.required');
    }
    
    if (!shippingAddress.address_line1) {
      errors.addressLine1 = t('errors.required');
    }
    
    if (!shippingAddress.city) {
      errors.city = t('errors.required');
    }
    
    if (!shippingAddress.postal_code) {
      errors.postalCode = t('errors.required');
    }
    
    if (!shippingAddress.country) {
      errors.country = t('errors.required');
    }
    
    // Validate billing address fields when not same as shipping
    if (!billingIsSameAsShipping) {
      if (!billingAddress.company_name) {
        errors.billingCompanyName = t('errors.required');
      }
      
      if (!billingAddress.address_line1) {
        errors.billingAddressLine1 = t('errors.required');
      }
      
      if (!billingAddress.city) {
        errors.billingCity = t('errors.required');
      }
      
      if (!billingAddress.postal_code) {
        errors.billingPostalCode = t('errors.required');
      }
      
      if (!billingAddress.country) {
        errors.billingCountry = t('errors.required');
      }
    }
    
    setFormErrors(errors);
    setFormValid(Object.keys(errors).length === 0);
  }, [email, companyName, shippingAddress, billingAddress, billingIsSameAsShipping, t, isB2B, paymentMethod, vatNumber]);

  // Mark a field as touched when it's blurred
  const handleBlur = (field: string) => {
    setFormTouched(prev => ({ ...prev, [field]: true }));
  };

  // Add effect to redirect if cart is empty
  useEffect(() => {
    // Only run this check after the component is fully hydrated and initial cart loading is done
    if (!isHydrated) return;
    
    // Skip the redirect if an order was just placed
    if (orderJustPlaced) return;
    
    const isCartEmpty = 
      (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) && 
      (!displayItems || !Array.isArray(displayItems) || displayItems.length === 0) && 
      (!enrichedCartItems || !Array.isArray(enrichedCartItems) || enrichedCartItems.length === 0);
    
    if (isCartEmpty && !isLoading) {
      // Wait a moment before redirecting to ensure we're not in the middle of loading
      const redirectTimer = setTimeout(() => {
        router.push(`/${locale}/products`);
      }, 500);
      
      return () => clearTimeout(redirectTimer);
    }
  }, [cartItems, displayItems, enrichedCartItems, isLoading, router, locale, isHydrated, orderJustPlaced]);

  // Update the shipping address whenever it changes
  useEffect(() => {
    // Skip if values are identical to prevent update loops
    if (
      prevShippingAddress.current.company_name === shippingAddress.company_name &&
      prevShippingAddress.current.address_line1 === shippingAddress.address_line1 &&
      prevShippingAddress.current.address_line2 === shippingAddress.address_line2 &&
      prevShippingAddress.current.city === shippingAddress.city &&
      prevShippingAddress.current.state === shippingAddress.state &&
      prevShippingAddress.current.postal_code === shippingAddress.postal_code &&
      prevShippingAddress.current.country === shippingAddress.country &&
      prevShippingAddress.current.notes === shippingAddress.notes
    ) {
      return;
    }
    
    // Update the previous value
    prevShippingAddress.current = { ...shippingAddress };
    
    // Update the store
    checkoutState.updateShippingAddress(shippingAddress);
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [shippingAddress]);

  // Update the other form fields when they change
  useEffect(() => {
    if (prevEmail.current !== email) {
      prevEmail.current = email;
      checkoutState.updateEmail(email);
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [email]);

  useEffect(() => {
    if (prevPhone.current !== phone) {
      prevPhone.current = phone;
      checkoutState.updatePhone(phone);
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [phone]);

  useEffect(() => {
    if (prevCompanyName.current !== companyName) {
      prevCompanyName.current = companyName;
      checkoutState.updateCompanyName(companyName);
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [companyName]);

  useEffect(() => {
    if (prevVatNumber.current !== vatNumber || prevVatValid.current !== vatValid) {
      prevVatNumber.current = vatNumber;
      prevVatValid.current = vatValid;
      checkoutState.updateVat(vatNumber, vatValid, vatValidatedCompanyName);
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [vatNumber, vatValid, vatValidatedCompanyName]);

  useEffect(() => {
    if (prevPaymentMethod.current !== paymentMethod) {
      prevPaymentMethod.current = paymentMethod;
      checkoutState.updatePaymentMethod(paymentMethod);
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [paymentMethod]);

  // Add effect to update billing company name when billingIsSameAsShipping changes and VAT is valid
  useEffect(() => {
    // Case 1: When toggling from "same" to "different" and VAT is valid
    if (prevBillingIsSameAsShipping.current === true && 
        billingIsSameAsShipping === false) {
      
      // If VAT is valid, use validated company name
      if (vatValid && vatValidatedCompanyName) {
        setBillingAddress(prev => ({
          ...prev,
          company_name: vatValidatedCompanyName
        }));
      } 
      // Otherwise use shipping company name as fallback
      else if (companyName) {
        setBillingAddress(prev => ({
          ...prev,
          company_name: companyName
        }));
      }
    }
    
    // Case 2: When toggling from "different" back to "same" and VAT is valid
    if (prevBillingIsSameAsShipping.current === false && 
        billingIsSameAsShipping === true && 
        vatValid) {
      
      // Reset shipping company name to VAT validated name if different
      if (companyName !== vatValidatedCompanyName) {
        setCompanyName(vatValidatedCompanyName);
      }
    }
    
    // Update the ref for next render
    prevBillingIsSameAsShipping.current = billingIsSameAsShipping;
    
    // Remove this line to prevent infinite loop - it's already handled by another effect
    // checkoutState.updateBillingIsSameAsShipping(billingIsSameAsShipping);
  }, [billingIsSameAsShipping, vatValid, companyName, vatValidatedCompanyName]);

  // Create a shared function to render validation warnings with missing fields
  const renderValidationWarning = () => {
    if (formValid) return null;
    
    const missingFields: string[] = [];
    
    // Check which fields have errors
    if (formErrors.email) missingFields.push(t('shipping.email'));
    if (formErrors.companyName) missingFields.push(isB2B ? t('shipping.fields.companyName') : t('shipping.fields.fullName'));
    if (formErrors.vatNumber) missingFields.push(t('shipping.fields.vatNumber'));
    if (formErrors.addressLine1) missingFields.push(t('shipping.fields.addressLine1'));
    if (formErrors.city) missingFields.push(t('shipping.fields.city'));
    if (formErrors.postalCode) missingFields.push(t('shipping.fields.postalCode'));
    if (formErrors.country) missingFields.push(t('shipping.fields.country'));
    
    return (
      <div className="mt-4 p-3 border border-status-warning bg-status-warning/30 rounded-lg text-text-main">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 pt-0.5">
            <svg className="h-5 w-5 text-status-warning" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-medium">{t('payment.completeFormFirst')}</h3>
            <p className="mt-1 text-xs">{t('payment.fillRequiredFields')}</p>
            
            {missingFields.length > 0 && (
              <div className="my-2">
                <p className="text-xs font-medium">{t('payment.missingFields') || 'Missing fields'}:</p>
                <ul className="mt-1 text-xs list-disc ml-4">
                  {missingFields.map((field, index) => (
                    <li key={index}>{field}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Set initial cart items after hydration is complete
  useEffect(() => {
    if (!isHydrated) return;
    
    // Set initial cart items if they exist
    if (initialCartItems && Array.isArray(initialCartItems) && initialCartItems.length > 0) {
      try {
        // Filter out any invalid cart items
        const validCartItems = initialCartItems.filter(item => 
          item && 
          item.product && 
          item.product.id
        );
        
        if (validCartItems.length > 0) {
          setCartItems(validCartItems);
        }
      } catch (error) {
        console.error('Error setting initial cart items:', error);
      }
    }
  }, [initialCartItems, isHydrated]);

  // Update shipping address fields in the checkout store
  useEffect(() => {
    if (JSON.stringify(prevShippingAddress.current) !== JSON.stringify(shippingAddress)) {
      prevShippingAddress.current = { ...shippingAddress };
      checkoutState.updateShippingAddress(shippingAddress);
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [shippingAddress]);
  
  // Update billing address fields in the checkout store
  useEffect(() => {
    if (JSON.stringify(prevBillingAddress.current) !== JSON.stringify(billingAddress)) {
      prevBillingAddress.current = { ...billingAddress };
      checkoutState.updateBillingAddress(billingAddress);
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [billingAddress]);
  
  // Update billingIsSameAsShipping in the checkout store
  useEffect(() => {
    checkoutState.updateBillingIsSameAsShipping(billingIsSameAsShipping);
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [billingIsSameAsShipping]);

  // Add a fallback mechanism to update billing company name when VAT is valid but billing company name is empty
  useEffect(() => {
    // Only run when VAT is valid and we have a validated company name
    if (vatValid && vatValidatedCompanyName && !billingIsSameAsShipping) {
      // If billing company name is empty, update it with the validated company name
      if (!billingAddress.company_name) {
        setBillingAddress(prev => ({
          ...prev,
          company_name: vatValidatedCompanyName
        }));
      }
    }
  }, [vatValid, vatValidatedCompanyName, billingIsSameAsShipping, billingAddress.company_name]);

  // Update the email field when user changes
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  // Add effect to update the truevoCheckoutKey when payment method changes
  useEffect(() => {
    if (paymentMethod === 'truevo') {
      // Force remount of TruevoCheckout when switching to card payment
      setTruevoCheckoutKey(Date.now());
    }
  }, [paymentMethod]);


  // Update payment method setter to reset the Truevo checkout when changing payment methods
  const handlePaymentMethodChange = (method: 'wire' | 'crypto' | 'truevo') => {
    // Don't allow changing payment method while submitting
    if (isSubmitting) return;
    
    // Reset error when changing payment method
    setError(null);
    
    // Update payment method
    setPaymentMethod(method);
    
    // Force remount of TruevoCheckout when switching back to card payment
    if (method === 'truevo') {
      setTimeout(() => {
        setTruevoCheckoutKey(Date.now());
      }, 50);
    }
  };

  // Get payment details from site settings
  const cryptoDetails = siteSettings?.payment_details?.crypto || { currency: 'USDT', address: '', network: '' }
  const wireDetails = siteSettings?.payment_details?.wire || { bankName: '', accountNumber: '', swift: '', iban: '', routingNumber: '' }

  // Hydrate currency cache with SSR siteSettings
  const [isCurrencyHydrated, setIsCurrencyHydrated] = useState(false);
  useEffect(() => {
    if (siteSettings) {
      updateI18nConfig(siteSettings);
      setIsCurrencyHydrated(true);
    }
  }, [siteSettings]);

  if (!isCurrencyHydrated) {
    return <div className="min-h-[200px] flex items-center justify-center"><span className="animate-pulse text-text-secondary">Loading prices...</span></div>;
  }

  // Handler to apply coupon code and compute discount
  const handleApplyCoupon = async () => {
    setIsApplyingCoupon(true);
    setCouponError(null);
    try {
      // Determine coupon subtotal: B2B uses gross, B2C uses net (exclude VAT)
      const subtotalForCoupon = isB2B ? baseTotal : baseTotal / 1.25;
      const result = await applyCoupon(couponCode.trim(), subtotalForCoupon, user?.id);
      if (result.valid) {
        setCouponId(result.couponId || null);
        setDiscountAmount(result.discountAmount || 0);
      } else {
        // If usage limit reached, mark coupon as used to disable further attempts
        if (result.error === 'You have reached the usage limit for this coupon.') {
          setCouponUsed(true);
        }
        setCouponError(result.error || 'Invalid coupon');
        setCouponId(null);
        setDiscountAmount(0);
      }
    } catch {
      setCouponError('Error applying coupon');
      setCouponId(null);
      setDiscountAmount(0);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-bg-main via-primary/50 to-primary/60">
      <div className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">

        {/* Display restricted products notification */}
        {restrictedProductsNotification && (
          <div className="mb-6 p-4 border border-status-warning bg-status-warning/30 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-status-warning" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-grow">
                <p className="text-sm text-status-warning-foreground">{restrictedProductsNotification}</p>
              </div>
              <div className="ml-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setRestrictedProductsNotification(null)}
                  className="inline-flex rounded-md p-1.5 text-status-warning-foreground hover:bg-status-warning/50 focus:outline-none focus:ring-2 focus:ring-status-warning"
                >
                  <span className="sr-only">{t('dismiss')}</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.586 6.28 5.22z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Main checkout grid - 2/3 left, 1/3 right */}
        <ClientOnly className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left 2/3 container */}
          <div className="lg:col-span-2 space-y-8">
            {/* Order Summary section */}
            <div className="bg-surface rounded-xl backdrop-blur-sm">
              <div className="p-6">
                <h1 className="text-2xl font-semibold text-primary">
                  {t('summary.title')}
                </h1>
              </div>
              <div>
                {displayItems.length === 0 ? (
                  <div className="p-6 text-center">
                    <div className="text-text-secondary">{t('summary.empty')}</div>
                    <Link href="/products" className="mt-3 inline-block text-primary">{t('summary.browseProducts')}</Link>
                  </div>
                ) : (
                  <>
                    <div className="max-h-80 overflow-y-auto">
                      {displayItems.map((item) => {
                        if (!item || !item.product) {
                          return (
                            <div key={`not-found-${Math.random()}`} className="p-6">
                              <p className="text-status-error">{t('summary.productNotFound')}</p>
                            </div>
                          );
                        }
                        
                        const itemKey = item.id || 
                          `${item.product.id}-${item.selectedColor || 'default'}-${Math.random().toString(36).substring(2, 9)}`;

                        const itemNetPrice = calculateApiItemPrice(item);
                        // For B2C, the base price is considered gross (includes VAT). For B2B, it is net.
                        const itemDisplayPrice = !isB2B ? itemNetPrice * 1.25 : itemNetPrice;
                        
                        return (
                          <div key={itemKey} className="p-6 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="flex-shrink-0 w-12 h-12 rounded-md flex items-center justify-center bg-surface-hover overflow-hidden relative shadow-sm">
                                  {item.product.product_image ? (
                                    <Image 
                                      src={item.product.product_image} 
                                      alt={translatedNames[item.product.id] || item.product.name} 
                                      width={48} 
                                      height={48}
                                      className="object-cover"
                                      placeholder="blur"
                                      blurDataURL="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNDgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgdmVyc2lvbj0iMS4xIi8+"
                                      onError={() => {
                                        // This is just to prevent potential console errors
                                        // The placeholder and fallback handle the visual appearance
                                      }}
                                    />
                                  ) : (
                                    // Default package box icon when no image is available
                                    <svg className="w-6 h-6 text-primary-light" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
                                    </svg>
                                  )}
                                </div>
                                <div>
                                  <div 
                                    onClick={() => router.push(`/${locale}/products/${translatedSlugs[item.product.id] || item.product.slug}`)}
                                    className="cursor-pointer hover:text-primary-light transition-colors"
                                  >
                                    <h3 className="text-lg font-medium text-text-main hover:text-primary">
                                      {translatedNames[item.product.id] || item.product.name}
                                    </h3>
                                  </div>
                                  <div className="flex items-center text-sm text-text-secondary space-x-4">
                                    <span>
                                      {t('summary.quantity')}: {
                                        item.product.kg_price !== false
                                          ? `${item.quantity}g`
                                          : `${item.quantity} ${item.quantity === 1 ? t('summary.unit') : t('summary.units')}`
                                      }
                                    </span>
                                    {item.selectedColor && (
                                      <span className="flex items-center">
                                        {t('summary.selected')}: {item.selectedColor}
                                        {getExtraPrice(item) > 0 && (
                                          <span className="ml-1 text-primary">
                                            {item.product.kg_price !== false 
                                              ? `(+${formatCurrency(
                                                  convertPriceWithRate(getExtraPrice(item), userCurrency).convertedAmount,
                                                  locale,
                                                  userCurrency
                                                )}/kg)`
                                              : `(+${formatCurrency(
                                                  convertPriceWithRate(getExtraPrice(item), userCurrency).convertedAmount,
                                                  locale,
                                                  userCurrency
                                                )})`
                                            }
                                          </span>
                                        )}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <p className="text-lg text-primary font-medium">
                                <DynamicPrice
                                  basePrice={itemDisplayPrice}
                                  vat_number={isVatValidForDisplay ? effectiveVatNumber : ''}
                                  vat_rate={0.25}
                                  forceRefresh={true}
                                  isB2B={isB2B}
                                />
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Fee Breakdown Section */}
                    <div className="p-3 bg-surface-hover rounded-lg text-sm mt-2 mx-6 space-y-4">
                      {/* Subtotal (before discount) */}
                      <div className="flex justify-between items-center">
                        <span className="text-text-secondary">{t('summary.subtotal')}</span>
                        <span className="text-text-main">
                          {formatCurrency(convertPriceWithRate(netSubtotalBeforeDiscount, userCurrency).convertedAmount, locale, userCurrency)}
                        </span>
                      </div>
                      {discountAmount > 0 && (
                        <div className="flex justify-between items-center">
                          <span className="text-text-secondary">{`${t('summary.discount')} (${couponCode.toUpperCase()})`}</span>
                          <span className="text-primary">- {formatCurrency(convertPriceWithRate(discountAmount, userCurrency).convertedAmount, locale, userCurrency)}</span>
                        </div>
                      )}
                      {paymentMethod === 'truevo' ? (
                        <>
                          {/* VAT line for Truevo */}
                          {!isB2B ? (
                            <div className="flex justify-between items-center">
                              <span className="text-text-secondary">
                                {isVatExemptB2C
                                  ? `${t('summary.vatIncluded')} (0%)`
                                  : `${t('summary.vatIncluded')} (25%)`}
                              </span>
                              <span className="text-primary">
                                {formatCurrency(
                                  convertPriceWithRate(isVatExemptB2C ? 0 : vatAmount, userCurrency).convertedAmount,
                                  locale,
                                  userCurrency
                                )}
                              </span>
                            </div>
                          ) : isVatValidForDisplay && !isCryptoPayment ? (
                            <div className="flex justify-between items-center">
                              <span className="text-text-secondary">{t('summary.vat')} (0%)</span>
                              <span className="text-primary">
                                {formatCurrency(
                                  convertPriceWithRate(0, userCurrency).convertedAmount,
                                  locale,
                                  userCurrency
                                )}
                              </span>
                            </div>
                          ) : (
                            <div className="flex justify-between items-center">
                              <span className="text-text-secondary">{t('summary.vat')} (25%)</span>
                              <span className="text-primary">
                                {formatCurrency(
                                  convertPriceWithRate(baseTotal * 0.25, userCurrency).convertedAmount,
                                  locale,
                                  userCurrency
                                )}
                              </span>
                            </div>
                          )}
                          {/* Transaction fee */}
                          <div className="flex justify-between items-center">
                            <span className="text-text-secondary">{t('summary.transactionFees')} (5%)</span>
                            <span className="text-primary">
                              {formatCurrency(
                                convertPriceWithRate(feeAmount, userCurrency).convertedAmount,
                                locale,
                                userCurrency
                              )}
                            </span>
                          </div>
                        </>
                      ) : (
                        <>
                          {!isB2B ? (
                            <div className="flex justify-between items-center">
                              <span className="text-text-secondary">
                                {isVatExemptB2C && !isCryptoPayment 
                                  ? `${t('summary.vatIncluded')} (0%)` 
                                  : `${t('summary.vatIncluded')} (25%)`}
                              </span>
                              <span className="text-primary">
                                {formatCurrency(
                                  convertPriceWithRate(
                                    isVatExemptB2C && !isCryptoPayment ? 0 : vatAmount, 
                                    userCurrency
                                  ).convertedAmount,
                                  locale,
                                  userCurrency
                                )}
                              </span>
                            </div>
                          ) : (
                            isVatValidForDisplay && !isCryptoPayment ? (
                              <div className="flex justify-between items-center">
                                <span className="text-text-secondary">{t('summary.vat')} (0%)</span>
                                <span className="text-primary">
                                  {formatCurrency(
                                    convertPriceWithRate(0, userCurrency).convertedAmount,
                                    locale,
                                    userCurrency
                                  )}
                                </span>
                              </div>
                            ) : (
                              <div className="flex justify-between items-center">
                                <span className="text-text-secondary">{t('summary.vat')} (25%)</span>
                                <span className="text-primary">
                                  {formatCurrency(
                                    convertPriceWithRate(baseTotal * 0.25, userCurrency).convertedAmount,
                                    locale,
                                    userCurrency
                                  )}
                                </span>
                              </div>
                            )
                          )}
                          <div className="flex justify-between items-center">
                            <span className="text-text-secondary">{t('summary.transactionFees')} {paymentMethod === 'crypto' ? '(10%)' : ''}</span>
                            <span className="text-primary">
                              {formatCurrency(
                                convertPriceWithRate(feeAmount, userCurrency).convertedAmount,
                                locale,
                                userCurrency
                              )}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex justify-between text-lg font-semibold">
                        <span className="text-text-main">{t('summary.total')}</span>
                        <span className="text-primary">
                          {formatCurrency(
                            convertPriceWithRate(finalTotal, userCurrency).convertedAmount,
                            locale,
                            userCurrency
                          )}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Shipping Address section */}
            <div className="bg-surface rounded-xl backdrop-blur-sm">
              <div className="p-6">
                <h2 className="text-2xl font-semibold text-primary flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  {t('shipping.title')}
                </h2>
              </div>
              
              <div className="p-6 pt-0">
                {error && (
                  <div className="mb-6 rounded-md bg-status-error/20 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-status-error" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-status-error">{error}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Address form grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Email - Full width */}
                  <div className="md:col-span-2">
                    <label htmlFor="email" className="block text-sm font-medium text-text-main">
                      {t('shipping.email')} *
                    </label>
                    <div className="mt-1">
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => handleBlur('email')}
                        disabled={!!user?.email} // Only disable if user is logged in and has an email
                        className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.email && formErrors.email ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}
                        required
                      />
                    </div>
                    {formTouched.email && formErrors.email && (
                      <p className="mt-1 text-xs text-status-error">{formErrors.email}</p>
                    )}
                    <p className="mt-1 text-xs text-text-secondary">
                      {user?.email 
                        ? t('shipping.emailFromAccount')
                        : t('shipping.emailHelp')}
                    </p>
                  </div>
                  
                  {/* Phone */}
                  <div className="md:col-span-2">
                    <label htmlFor="phone" className="block text-sm font-medium text-text-main">
                      {t('shipping.fields.phone')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="tel"
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.phone && formErrors.phone ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}
                      />
                    </div>
                  </div>
                  
                  {/* Company */}
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-text-main">
                      {isB2B ? t('shipping.fields.companyName') : t('shipping.fields.fullName')} *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="companyName"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        onBlur={() => handleBlur('companyName')}
                        disabled={vatValid && billingIsSameAsShipping}
                        className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.companyName && formErrors.companyName ? 'ring-1 ring-status-error focus:ring-status-error' : ''} ${vatValid ? 'opacity-70 cursor-not-allowed' : ''}`}
                        required
                      />
                    </div>
                    {formTouched.companyName && formErrors.companyName && (
                      <p className="mt-1 text-xs text-status-error">{formErrors.companyName}</p>
                    )}
                  </div>
                  
                  {/* VAT Number - Only required in B2B mode and not for bank transfer payments */}
                  <div>
                    <label htmlFor="vatNumber" className="block text-sm font-medium text-text-main">
                      {t('shipping.fields.vatNumber')} {isB2B && paymentMethod !== 'wire' ? '*' : ''}
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="text"
                        id="vatNumber"
                        value={vatNumber}
                        onChange={(e) => {
                          setVatNumber(e.target.value);
                          // If the user changes the VAT number, clear the tick if it no longer matches
                          if (
                            e.target.value !== (initialProfile?.vat_number || '') &&
                            e.target.value !== lastValidatedVat
                          ) {
                            setVatValid(false);
                          }
                        }}
                        onBlur={handleVatBlur}
                        className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.vatNumber && formErrors.vatNumber ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}
                        required={isB2B && paymentMethod !== 'wire'}
                      />
                      {showVatVerifiedTick && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg className="h-5 w-5 text-status-success" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {vatError && <p className="mt-1 text-xs text-status-error">{vatError}</p>}
                  </div>
                  
                  {/* Address Line 1 */}
                  <div className="md:col-span-2 overflow-visible relative">
                    <ErrorBoundary
                      FallbackComponent={ErrorFallback}
                      onReset={() => {
                        // Reset the component state here
                      }}
                    >
                      <GooglePlacesAutocomplete
                        label={t('shipping.fields.addressLine1')}
                        onAddressSelected={(address) => updateAddressFromPlacesApi(address, setShippingAddress)}
                        defaultValue={shippingAddress.address_line1}
                        required={true}
                        id="addressLine1"
                        placeholder={t('billing.fields.addressPlaceholder') || 'Start typing address...'}
                        className={formTouched.addressLine1 && formErrors.addressLine1 ? 'is-invalid' : ''}
                      />
                    </ErrorBoundary>
                    {formTouched.addressLine1 && formErrors.addressLine1 && (
                      <p className="mt-1 text-xs text-status-error">{formErrors.addressLine1}</p>
                    )}
                  </div>
                  
                  {/* Address Line 2 */}
                  <div className="md:col-span-2">
                    <label htmlFor="addressLine2" className="block text-sm font-medium text-text-main">
                      {t('shipping.fields.addressLine2')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="addressLine2"
                        value={shippingAddress.address_line2}
                        onChange={(e) => setShippingAddress({
                          ...shippingAddress,
                          address_line2: e.target.value
                        })}
                        className="bg-surface-hover shadow-sm block w-full sm:text-sm rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  
                  {/* City */}
                  <div>
                    <label htmlFor="city" className="block text-sm font-medium text-text-main">
                      {t('shipping.fields.city')} *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="city"
                        value={shippingAddress.city}
                        onChange={(e) => setShippingAddress({
                          ...shippingAddress,
                          city: e.target.value
                        })}
                        onBlur={() => handleBlur('city')}
                        className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.city && formErrors.city ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}
                        required
                      />
                    </div>
                    {formTouched.city && formErrors.city && (
                      <p className="mt-1 text-xs text-status-error">{formErrors.city}</p>
                    )}
                  </div>
                  
                  {/* State/Province */}
                  <div>
                    <label htmlFor="state" className="block text-sm font-medium text-text-main">
                      {t('shipping.fields.state')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="state"
                        value={shippingAddress.state}
                        onChange={(e) => setShippingAddress({
                          ...shippingAddress,
                          state: e.target.value
                        })}
                        className="bg-surface-hover shadow-sm block w-full sm:text-sm rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>
                  
                  {/* Postal Code */}
                  <div>
                    <label htmlFor="postalCode" className="block text-sm font-medium text-text-main">
                      {t('shipping.fields.postalCode')} *
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        id="postalCode"
                        value={shippingAddress.postal_code}
                        onChange={(e) => setShippingAddress({
                          ...shippingAddress,
                          postal_code: e.target.value
                        })}
                        onBlur={() => handleBlur('postalCode')}
                        className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.postalCode && formErrors.postalCode ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}
                        required
                      />
                    </div>
                    {formTouched.postalCode && formErrors.postalCode && (
                      <p className="mt-1 text-xs text-status-error">{formErrors.postalCode}</p>
                    )}
                  </div>
                  
                  {/* Country */}
                  <div>
                    <label htmlFor="country" className="block text-sm font-medium text-text-main">
                      {t('shipping.fields.country')} *
                    </label>
                    <div className="mt-1">
                      <select
                        id="country"
                        value={shippingAddress.country}
                        onChange={(e) => setShippingAddress({
                          ...shippingAddress,
                          country: e.target.value
                        })}
                        onBlur={() => handleBlur('country')}
                        className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.country && formErrors.country ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}
                        required
                      >
                        <option value="">{t('shipping.fields.selectCountry')}</option>
                        {countries.map((country) => (
                          <option key={country} value={country}>
                            {countryT(country)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Notes */}
                  <div className="md:col-span-2">
                    <label htmlFor="notes" className="block text-sm font-medium text-text-main">
                      {t('shipping.fields.notes')}
                    </label>
                    <div className="mt-1">
                      <textarea
                        id="notes"
                        value={shippingAddress.notes}
                        onChange={(e) => setShippingAddress({
                          ...shippingAddress,
                          notes: e.target.value
                        })}
                        rows={3}
                        className="bg-surface-hover shadow-sm block w-full sm:text-sm rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  </div>

                  {/* Billing Address Checkbox */}
                  <div className="md:col-span-2 border-t border-border pt-4 mt-2">
                    <div className="flex items-center mb-4">
                      <input
                        id="billing-same-as-shipping"
                        type="checkbox"
                        checked={billingIsSameAsShipping}
                        onChange={(e) => setBillingIsSameAsShipping(e.target.checked)}
                        className="h-4 w-4 text-primary focus:ring-primary focus:ring-offset-0 bg-surface-hover rounded accent-primary"
                      />
                      <label htmlFor="billing-same-as-shipping" className="ml-2 block text-sm font-medium text-text-main">
                        {t('billing.sameAsShipping') || 'Billing address is same as shipping address'}
                      </label>
                    </div>
                  </div>
                </div>
                
                {/* Show billing address form if not same as shipping */}
                {!billingIsSameAsShipping && (
                  <div className="mt-4 rounded-lg bg-surface">
                    <h3 className="text-lg font-medium text-primary mb-4">{t('billing.title') || 'Billing Address'}</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Billing Company */}
                      <div>
                        <label htmlFor="billingCompanyName" className="block text-sm font-medium text-text-main">
                          {isB2B ? t('billing.fields.companyName') : t('billing.fields.fullName')} *
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="billingCompanyName"
                            value={billingAddress.company_name}
                            onChange={(e) => setBillingAddress({
                              ...billingAddress,
                              company_name: e.target.value
                            })}
                            onBlur={() => handleBlur('billingCompanyName')}
                            disabled={vatValid}
                            className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.billingCompanyName && formErrors.billingCompanyName ? 'ring-1 ring-status-error focus:ring-status-error' : ''} ${vatValid ? 'opacity-70 cursor-not-allowed' : ''}`}
                            required
                          />
                        </div>
                        {formTouched.billingCompanyName && formErrors.billingCompanyName && (
                          <p className="mt-1 text-xs text-status-error">{formErrors.billingCompanyName}</p>
                        )}
                      </div>
                      
                      {/* Spacer to match shipping address layout */}
                      <div></div>
                      
                      {/* Billing Address Line 1 */}
                      <div className="md:col-span-2">
                        <div className={`${formTouched.billingAddressLine1 && formErrors.billingAddressLine1 ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}>
                          <GooglePlacesAutocomplete
                            label={t('billing.fields.addressLine1')}
                            onAddressSelected={(address) => {
                              setBillingAddress(prev => ({
                                ...prev,
                                address_line1: address.address_line1 || prev.address_line1,
                                address_line2: address.address_line2 || prev.address_line2,
                                city: address.city || prev.city,
                                state: address.state || prev.state,
                                postal_code: address.postal_code || prev.postal_code,
                                country: address.country || prev.country
                              }));
                            }}
                            defaultValue={billingAddress.address_line1}
                            required={true}
                            id="billing-address-autocomplete"
                            placeholder={t('billing.fields.addressPlaceholder') || 'Start typing address...'}
                          />
                        </div>
                        {formTouched.billingAddressLine1 && formErrors.billingAddressLine1 && (
                          <p className="mt-1 text-xs text-status-error">{formErrors.billingAddressLine1}</p>
                        )}
                      </div>
                      
                      {/* Billing Address Line 2 */}
                      <div className="md:col-span-2">
                        <label htmlFor="billingAddressLine2" className="block text-sm font-medium text-text-main">
                          {t('billing.fields.addressLine2') || 'Address Line 2'}
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="billingAddressLine2"
                            value={billingAddress.address_line2}
                            onChange={(e) => setBillingAddress({
                              ...billingAddress,
                              address_line2: e.target.value
                            })}
                            className="bg-surface-hover shadow-sm block w-full sm:text-sm rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                      
                      {/* Billing City */}
                      <div>
                        <label htmlFor="billingCity" className="block text-sm font-medium text-text-main">
                          {t('billing.fields.city') || 'City'} *
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="billingCity"
                            value={billingAddress.city}
                            onChange={(e) => setBillingAddress({
                              ...billingAddress,
                              city: e.target.value
                            })}
                            onBlur={() => handleBlur('billingCity')}
                            className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.billingCity && formErrors.billingCity ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}
                            required
                          />
                        </div>
                        {formTouched.billingCity && formErrors.billingCity && (
                          <p className="mt-1 text-xs text-status-error">{formErrors.billingCity}</p>
                        )}
                      </div>
                      
                      {/* Billing State/Province */}
                      <div>
                        <label htmlFor="billingState" className="block text-sm font-medium text-text-main">
                          {t('billing.fields.state') || 'State/Province'}
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="billingState"
                            value={billingAddress.state}
                            onChange={(e) => setBillingAddress({
                              ...billingAddress,
                              state: e.target.value
                            })}
                            className="bg-surface-hover shadow-sm block w-full sm:text-sm rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>
                      
                      {/* Billing Postal Code */}
                      <div>
                        <label htmlFor="billingPostalCode" className="block text-sm font-medium text-text-main">
                          {t('billing.fields.postalCode') || 'Postal Code'} *
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="billingPostalCode"
                            value={billingAddress.postal_code}
                            onChange={(e) => setBillingAddress({
                              ...billingAddress,
                              postal_code: e.target.value
                            })}
                            onBlur={() => handleBlur('billingPostalCode')}
                            className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.billingPostalCode && formErrors.billingPostalCode ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}
                            required
                          />
                        </div>
                        {formTouched.billingPostalCode && formErrors.billingPostalCode && (
                          <p className="mt-1 text-xs text-status-error">{formErrors.billingPostalCode}</p>
                        )}
                      </div>
                      
                      {/* Billing Country */}
                      <div>
                        <label htmlFor="billingCountry" className="block text-sm font-medium text-text-main">
                          {t('billing.fields.country') || 'Country'} *
                        </label>
                        <div className="mt-1">
                          <select
                            id="billingCountry"
                            value={billingAddress.country}
                            onChange={(e) => setBillingAddress({
                              ...billingAddress,
                              country: e.target.value
                            })}
                            onBlur={() => handleBlur('billingCountry')}
                            className={`shadow-sm block w-full sm:text-sm bg-surface-hover rounded-md p-2 text-text-main focus:outline-none focus:ring-2 focus:ring-primary ${formTouched.billingCountry && formErrors.billingCountry ? 'ring-1 ring-status-error focus:ring-status-error' : ''}`}
                            required
                          >
                            <option value="">{t('shipping.fields.selectCountry')}</option>
                            {countries.map((country) => (
                              <option key={country} value={country}>
                                {countryT(country)}
                              </option>
                            ))}
                          </select>
                        </div>
                        {formTouched.billingCountry && formErrors.billingCountry && (
                          <p className="mt-1 text-xs text-status-error">{formErrors.billingCountry}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Right 1/3 container */}
          <div className="space-y-8">
            {/* Coupon Input section - ADDED HERE */}
            <CouponInput
              baseTotal={baseTotal}
              locale={locale}
              couponCode={couponCode}
              onCouponCodeChange={setCouponCode}
              onApply={handleApplyCoupon}
              isLoading={isApplyingCoupon}
              error={couponError}
              isApplied={!!couponId || couponUsed}
            />

            {/* Payment Methods section */}
            <div className="bg-surface rounded-xl backdrop-blur-sm">
              <div className="p-6">
                <h2 className="text-2xl font-semibold text-primary flex items-center">
                  <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  {t('payment.title')}
                </h2>
              </div>
              
              <div className="p-6 pt-0">
                <div className="space-y-4">
                  {availablePaymentMethods.map((method) => (
                    <div key={method.id} className="flex items-center">
                      <input
                        id={`payment-${method.id}`}
                        name="payment-method"
                        type="radio"
                        checked={paymentMethod === method.id}
                        onChange={() => handlePaymentMethodChange(method.id as 'wire' | 'crypto' | 'truevo')}
                        className="h-4 w-4 text-primary focus:ring-primary focus:ring-offset-0 bg-surface-active border-none rounded-full accent-primary appearance-none checked:bg-primary relative overflow-hidden"
                      />
                      <label htmlFor={`payment-${method.id}`} className="ml-3 flex items-center">
                        <span className="block text-sm text-text-main">{method.name}</span>
                        {method.icon && (
                          <span className="ml-2 text-text-secondary">{method.icon}</span>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
                
                {/* Payment Method Content */}
                <div className="mt-6">
                  {/* Truevo Credit Card Payment */}
                  {paymentMethod === 'truevo' && (
                    <TruevoMethod
                      truevoCheckoutKey={truevoCheckoutKey}
                      amount={Number((netTotal + feeAmount + (isVatExemptB2C ? 0 : baseTotal * 0.25)).toFixed(2))}
                      currencyCode="EUR"
                      customerInfo={{
                        firstName: companyName,
                        lastName: '',
                        email,
                        phone: phone || '',
                        address: {
                          street1: billingIsSameAsShipping ? shippingAddress.address_line1 : billingAddress.address_line1,
                          street2: billingIsSameAsShipping ? shippingAddress.address_line2 : billingAddress.address_line2,
                          city: billingIsSameAsShipping ? shippingAddress.city : billingAddress.city,
                          state: billingIsSameAsShipping ? shippingAddress.state : billingAddress.state,
                          postCodeZip: billingIsSameAsShipping ? shippingAddress.postal_code : billingAddress.postal_code,
                          countryAlphaCode: billingIsSameAsShipping ? countryCodeMap[shippingAddress.country] || 'GB' : countryCodeMap[billingAddress.country] || 'GB'
                        }
                      }}
                      formValid={formValid}
                      renderValidationWarning={renderValidationWarning}
                      onTransactionCompleted={async (response: TruevoResponse) => {
                        setTruevoCheckoutKey(prevKey => prevKey);
                        
                        // Extract payment details from the Truevo response
                        // Access data safely considering the structure of the response
                        const responseData = response.data as Record<string, any> || {};
                        const transactionInfo = responseData.transactionInfo || (response as any).transactionInfo || {};
                        
                        const transactionId = transactionInfo.transactionId || response.transactionId || '';
                        const paymentReference = transactionInfo.merchantReference || response.merchantReference || '';
                        // This is for the order status (needs to be "processing")
                        const paymentStatus = "processing";
                        // This is for the payment_status column (use actual payment status)
                        let actualPaymentStatus = response.paymentStatus || transactionInfo.status || "complete";
                        if (response.paymentStatus === "complete") {
                          actualPaymentStatus = "paid";
                        } else if (response.paymentStatus === "pending") {
                          actualPaymentStatus = "pending";
                        } else if (response.paymentStatus === "failed") {
                          actualPaymentStatus = "failed";
                        }
                        
                        // Process the successful payment
                        setIsSubmitting(true);
                        setError(null);

                        const loadingOverlay = document.createElement('div');
                        loadingOverlay.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50';
                        loadingOverlay.innerHTML = `
                          <div class="bg-surface p-6 rounded-lg border border-primary shadow-xl">
                            <div class="flex items-center space-x-4">
                              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                              <p class="text-primary-light">${t('payment.processingOrder')}</p>
                            </div>
                          </div>
                        `;
                        document.body.appendChild(loadingOverlay);
                       
                        try {
                          
                          // Prepare payment details from Truevo response
                          const paymentDetails = {
                            status: paymentStatus,
                            method: 'truevo',
                            transactionId: transactionId,
                            paymentReference: paymentReference,
                            merchantReference: paymentReference,
                            paymentStatus: actualPaymentStatus, 
                            responseCode: transactionInfo.responseCode || response.responseCode,
                            transactionStatus: transactionInfo.transactionStatus || response.transactionStatus
                          };
                          
                          const currentUser = user ? { id: user.id } : null;
                          const apiCartItems = displayItems
                            .filter(item => item && item.product)
                            .map(item => ({
                              product: item.product,
                              quantity: item.quantity,
                              selectedColor: item.selectedColor
                            }));
                          
                          const finalShippingAddress = {
                            ...shippingAddress,
                            company_name: companyName || shippingAddress.company_name
                          };

                          // Calculate values for the request
                          const baseTotal = displayItems
                            .filter(item => item && item.product)
                            .reduce((sum, item) => {
                              if (!item || !item.product) return sum;
                              const itemPrice = calculateApiItemPrice(item);
                              return sum + (isNaN(itemPrice) ? 0 : itemPrice);
                            }, 0);

                          const transactionFee = baseTotal * 0.05;
                          const vatAmount = vatNumber.trim() ? 0 : baseTotal * 0.25;

                          // Create the order through the API with payment details
                          const requestPayload = {
                            user: currentUser,
                            cartItems: apiCartItems,
                            shippingAddress: finalShippingAddress,
                            billingAddress: billingIsSameAsShipping ? finalShippingAddress : billingAddress,
                            paymentMethod: 'truevo',
                            isGuestCheckout: !user,
                            email,
                            locale,
                            vat_number: vatValid ? vatNumber.trim() : undefined,
                            payment_reference: paymentReference,
                            transaction_id: transactionId,
                            payment_status: actualPaymentStatus,
                            payment: paymentDetails,
                            baseTotal: Number(baseTotal.toFixed(2)),
                            vatAmount: Number(vatAmount.toFixed(2)),
                            transactionFee: Number(transactionFee.toFixed(2)),
                            coupon: couponId ? couponCode.trim() : null,
                            coupon_discount: couponId ? (isB2B ? discountAmount : discountAmount * 1.25).toFixed(2) : null,
                            finalTotal: Number((netTotal + feeAmount).toFixed(2)),
                            notes: shippingAddress.notes
                          };
                          
                          const apiResponse = await fetch('/api/createOrder', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(requestPayload)
                          });
                          
                          const result = await apiResponse.json();
                          
                          if (apiResponse.ok && result.success) {
                            clearCart();
                            
                            document.body.removeChild(loadingOverlay);
                            
                            if (!user && result.orderToken) {
                              router.push(`/${locale}/orders/${result.orderId}?token=${encodeURIComponent(result.orderToken)}`);
                            } else {
                              router.push(`/${locale}/orders/${result.orderId}`);
                            }
                          } else {
                            console.error('Error creating order after successful payment:', result.error);
                            setError(result.error || t('errors.createOrderFailed', {}));
                            setIsSubmitting(false);
                            document.body.removeChild(loadingOverlay);
                          }
                        } catch (err) {
                          console.error('Error processing successful payment:', err);
                          setError(t('errors.generalError'));
                          setIsSubmitting(false);
                          if (document.body.contains(loadingOverlay)) {
                            document.body.removeChild(loadingOverlay);
                          }
                        }
                      }}
                      onTransactionDeclined={(response: TruevoResponse) => {
                        console.log('Transaction declined:', response);
                        setError(t('payment.declined'));
                      }}
                      onTransactionFailed={(response: TruevoResponse) => {
                        console.log('Transaction failed:', response);
                        setError(t('payment.failed'));
                      }}
                    />
                  )}
                  
                  {/* Bank Transfer Payment */}
                  {paymentMethod === 'wire' && (
                    <WireMethod
                      wireDetails={wireDetails}
                      formValid={formValid}
                      renderValidationWarning={renderValidationWarning}
                      isSubmitting={isSubmitting}
                      handleCheckout={handleCheckout}
                    />
                  )}
                  
                  {/* Crypto Payment */}
                  {paymentMethod === 'crypto' && (
                    <CryptoMethod
                      cryptoDetails={cryptoDetails}
                      formValid={formValid}
                      renderValidationWarning={renderValidationWarning}
                      isSubmitting={isSubmitting}
                      handleCheckout={handleCheckout}
                    />
                  )}
                </div>
              </div>
            </div>
            
            {/* Order Terms and Privacy */}
            <div className="bg-surface/50 rounded-xl border border-border p-4 text-xs text-text-secondary">
              <p className="mb-2">
                {t('termsNote')} <Link href="/terms" className="text-primary hover:text-primary-light">{t('termsLink')}</Link> {t('and')} <Link href="/privacy" className="text-primary hover:text-primary-light">{t('privacyLink')}</Link>.
              </p>
              <p>
                {t('dataNote')}
              </p>
            </div>
          </div>
        </ClientOnly>
      </div>
    </div>
  )
}